// @givmo/org-console — the Profile page's PREVIEW tab (PE-WEB1): "View as public".
//
// The same composition the public renderers fetch (`GET /public/profiles/{type}/{id}/page`),
// rendered in preview context. It is honest in both directions: when the public read answers a
// page, the blocks render in owner order exactly as a visitor sees them (consecutive links as one
// list, a video behind a click-to-play facade, images lazy); when the public read answers 404 the
// tab shows the SUPPRESSION banner and nothing else — never a preview of a page nobody can see
// (plan §1.5 / §4). The organization's real public surface lives in another app, so "the same
// renderer" is a data statement (same read, two authorities), and the tab links to it when the
// host has an address for it.
//
// Testids are PER BLOCK — `{prefix}-block-{kind}-{blockId}` (a coalesced list keys on its first
// link's id; each row is `{prefix}-link-{linkId}`) with `data-block-kind` beside them — so an e2e
// spec that seeded the blocks selects one by id, never by position. The donate page uses the same
// scheme (`donate-profile-block-…`).
//
// ⚠ A video iframe mounts ONLY on click, and only on an embed URL this client re-validates against
// the two canonical hosts (§3.7). The server stores the canonical form; a value that is not one
// renders as a placeholder, never as a frame on an author-supplied host.

import type { ReactNode } from 'react';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Icon,
  VideoFacade,
  testIdProp,
} from '@givmo/design-system';
import { EyeOff, ExternalLink, LayoutTemplate } from 'lucide-react';
import { PROFILE_COPY } from './profile-copy';
import { composeForRender, type ProfilePageVocab, type RenderItem } from './profile-model';
import type { ProfileStatisticFigure, ProfileStatisticWithheld } from './profile-statistics';
import type { ProfileStatisticsVocab } from './profile-statistics-card';
import { ProfileStatisticsStrip } from './profile-statistics-strip';
import type { PublicProfilePageOut } from '@givmo/api-client';

/**
 * The Statistics block for View as public (PE-WEB15): the figures the PUBLIC sees — the owner read's
 * `public_view`, which the server computes for a signed-in person outside the organization, never
 * the admin's own view — and the stats it withholds from them, with why.
 */
export interface ProfilePreviewStatistics {
  figures: readonly ProfileStatisticFigure[];
  withheld: readonly ProfileStatisticWithheld[];
  vocab: ProfileStatisticsVocab;
}

export type ProfilePreviewState =
  | { kind: 'loading' }
  /** The OWNER read answered 404: page content is not switched on for this organization's class. */
  | { kind: 'unavailable' }
  /** The owner read answers but the PUBLIC read answered 404: the page is not served to anyone. */
  | { kind: 'suppressed' }
  | { kind: 'error'; message: string; onRetry: () => void }
  | { kind: 'served'; page: PublicProfilePageOut };

export interface ProfilePreviewProps {
  state: ProfilePreviewState;
  vocab: ProfilePageVocab;
  /**
   * The class's PUBLIC header, exactly as its public surface shows it — the host renders it from
   * its own entity read (one class: its donate page's hero; another: its app header plus the
   * public facts). It sits above the blocks inside the served composition (D-PE-17 (6)).
   */
  header?: PublicHeaderSlot;
  /** The host's QR code card, directly under the public-page link or statement; absent ⇒ none. */
  publicQr?: ReactNode;
  /**
   * The host's line on who can see the public page, directly under the public-page link or
   * statement — a door onto its visibility settings (EP-MNT5(c)); absent ⇒ none, and
   * the heading row renders exactly as without it.
   */
  publicVisibility?: ReactNode;
  /** The Statistics block, drawn under the header (D-PE-41); absent ⇒ none. */
  statistics?: ProfilePreviewStatistics;
  /** Told once per block of a kind this client does not know (§1.2) — the host's telemetry. */
  onUnknownKind?: (kind: string) => void;
  testIdPrefix: string;
}

/** A node, or a function of the served page (a host that renders the public read's `facts`). */
export type PublicHeaderSlot = ReactNode | ((page: PublicProfilePageOut) => ReactNode);

export function ProfilePreview({
  state,
  vocab,
  header,
  publicQr,
  publicVisibility,
  statistics,
  onUnknownKind,
  testIdPrefix,
}: ProfilePreviewProps) {
  const prefix = `${testIdPrefix}-preview`;
  const publicPlace = vocab.publicPageHref ? (
    <a
      href={vocab.publicPageHref}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-6 font-sans text-s13 font-w600 tracking-givmo text-brand-700 underline max-lg:min-h-44"
      {...testIdProp(`${prefix}-open-public`)}
    >
      {PROFILE_COPY.preview.openPublic}
      <Icon icon={ExternalLink} size={14} aria-hidden />
    </a>
  ) : vocab.publicPageStatement ? (
    <p
      className="font-sans text-s13 tracking-givmo text-text-secondary"
      {...testIdProp(`${prefix}-public-statement`)}
    >
      {vocab.publicPageStatement}
    </p>
  ) : null;
  return (
    <section className="flex flex-col gap-16 pt-16" {...testIdProp(prefix)}>
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="min-w-0">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {PROFILE_COPY.preview.heading}
          </h2>
          {/* text-secondary, not tertiary: tertiary on the page background is 4.47:1, under the
              4.5:1 floor for body text (PE-WEB12). */}
          <p className="mt-2 font-sans text-s13 tracking-givmo text-text-secondary">
            {PROFILE_COPY.preview.lede}
          </p>
        </div>
        {publicVisibility === undefined || publicVisibility === null ? (
          publicPlace
        ) : (
          <div className="flex flex-col gap-4 sm:items-end">
            {publicPlace}
            {publicVisibility}
          </div>
        )}
      </div>

      {publicQr}

      {state.kind === 'loading' && (
        <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-loading`)}>
          {PROFILE_COPY.common.loading}
        </p>
      )}

      {state.kind === 'unavailable' && (
        <EmptyState
          testId={`${prefix}-unavailable`}
          icon={<Icon icon={LayoutTemplate} size={20} aria-hidden />}
          title={PROFILE_COPY.preview.unavailable.title}
          description={PROFILE_COPY.preview.unavailable.body}
        />
      )}

      {state.kind === 'suppressed' && (
        <Card
          className="border-warning-100 bg-warning-50"
          testId={`${prefix}-banner`}
          role="status"
        >
          <CardBody className="flex items-start gap-12">
            <Icon icon={EyeOff} size={18} aria-hidden className="mt-2 shrink-0 text-warning-700" />
            <div className="min-w-0">
              <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                {PROFILE_COPY.preview.suppressed.title}
              </p>
              <p className="mt-4 font-sans text-s13 tracking-givmo text-text-secondary">
                {PROFILE_COPY.preview.suppressed.body}
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {state.kind === 'error' && (
        <Card testId={`${prefix}-error`}>
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 text-text-secondary">{state.message}</p>
            <Button appearance="saas" tone="secondary" size="sm" onClick={state.onRetry}>
              {PROFILE_COPY.common.retry}
            </Button>
          </CardBody>
        </Card>
      )}

      {state.kind === 'served' && (
        <PublicComposition
          page={state.page}
          header={header}
          statistics={statistics}
          interstitial={vocab.publicPageInterstitial ?? null}
          orgName={vocab.orgName?.trim() || null}
          onUnknownKind={onUnknownKind}
          prefix={prefix}
        />
      )}
    </section>
  );
}

function PublicComposition({
  page,
  header,
  statistics,
  interstitial,
  orgName,
  onUnknownKind,
  prefix,
}: {
  page: PublicProfilePageOut;
  header: PublicHeaderSlot;
  statistics: ProfilePreviewStatistics | undefined;
  interstitial: string | null;
  orgName: string | null;
  onUnknownKind: ((kind: string) => void) | undefined;
  prefix: string;
}) {
  const items = composeForRender(page.blocks, onUnknownKind ? { onUnknownKind } : {});
  const headerNode = typeof header === 'function' ? header(page) : header;
  return (
    <Card testId={`${prefix}-page`}>
      <CardBody className="flex flex-col gap-24">
        {headerNode !== undefined && headerNode !== null && (
          <div {...testIdProp(`${prefix}-header`)}>{headerNode}</div>
        )}
        {/* Givmo's figures where the apps draw the strip: under the header, above the organization's
            own blocks and their "From {organization}" eyebrow (D-PE-41). */}
        {statistics !== undefined && (
          <ProfileStatisticsStrip
            figures={statistics.figures}
            withheld={statistics.withheld}
            vocab={statistics.vocab}
            testIdPrefix={prefix}
          />
        )}
        {interstitial !== null && interstitial.length > 0 && (
          <p
            className="rounded-all12 border border-dashed border-border px-16 py-12 text-center font-sans text-s13 tracking-givmo text-text-tertiary"
            {...testIdProp(`${prefix}-interstitial`)}
          >
            {interstitial}
          </p>
        )}
        {items.length === 0 ? (
          <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-empty`)}>
            {PROFILE_COPY.preview.empty}
          </p>
        ) : (
          <>
            {/* The attribution eyebrow the public surface draws above the blocks ("From {organization}")
                — the organization's own words, marked off from the host's (D-PE-17 (6): the preview
                shows the page exactly as the public surface does). */}
            {orgName !== null && (
              <p
                className="-mb-12 font-sans text-s12 font-w700 tracking-[0.08em] text-text-tertiary uppercase"
                {...testIdProp(`${prefix}-attribution`)}
              >
                {PROFILE_COPY.preview.attribution(orgName)}
              </p>
            )}
            {items.map((item) => (
              <RenderBlock key={item.key} item={item} prefix={prefix} />
            ))}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function RenderBlock({ item, prefix }: { item: RenderItem; prefix: string }) {
  switch (item.kind) {
    case 'story':
      return (
        <article data-block-kind="story" {...testIdProp(`${prefix}-block-story-${item.key}`)}>
          {item.title && (
            <h3 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              {item.title}
            </h3>
          )}
          {/* Plain text end-to-end (§3.7): `pre-line` keeps the author's paragraphs, nothing is interpreted. */}
          <p className="mt-4 font-sans text-s14 leading-h15 tracking-givmo whitespace-pre-line text-text">
            {item.body}
          </p>
        </article>
      );
    case 'image':
      return (
        <figure data-block-kind="image" {...testIdProp(`${prefix}-block-image-${item.key}`)}>
          {item.src ? (
            <img
              src={item.src}
              alt={item.alt}
              loading="lazy"
              className="max-h-[420px] w-full rounded-all12 object-cover"
            />
          ) : (
            <div className="flex h-[160px] w-full items-center justify-center rounded-all12 bg-surface-sunken font-sans text-s13 text-text-tertiary">
              {item.alt || '—'}
            </div>
          )}
          {item.alt && (
            <figcaption className="mt-4 font-sans text-s13 tracking-givmo text-text-tertiary">
              {item.alt}
            </figcaption>
          )}
        </figure>
      );
    case 'video':
      return (
        <VideoFacade
          embedUrl={item.embedUrl}
          posterUrl={item.posterUrl}
          providerLabel={item.provider}
          title={item.title}
          playLabel={PROFILE_COPY.preview.playVideo}
          testId={`${prefix}-block-video-${item.key}`}
        />
      );
    case 'links':
      return (
        <ul
          className="flex flex-col gap-8"
          data-block-kind="links"
          {...testIdProp(`${prefix}-block-links-${item.key}`)}
        >
          {item.links.map((link) => (
            <li key={link.key}>
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-12 rounded-all12 border border-border px-16 py-12 hover:bg-surface-hover"
                {...testIdProp(`${prefix}-link-${link.key}`)}
              >
                <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                  {link.label}
                </span>
                <span className="shrink-0 font-sans text-s12 tracking-givmo text-text-tertiary">
                  {link.host}
                </span>
              </a>
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}
